/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
  // ATK currency selector live sync: 2026-06-01.
  var DISPLAY_CURRENCY_KEY = 'atk_display_currency';
  var RATE_CACHE_KEY = 'atk_currency_rates_v2';
  var FALLBACK_CURRENCY_RATES = {
    USD: 1,
    JPY: 163.83,
    EUR: 0.8787,
    KRW: 1457.52,
    GBP: 0.7525,
    CAD: 1.4104,
    AUD: 1.4338,
    SGD: 1.2925,
    HKD: 7.8417,
    CNY: 6.7785,
    TWD: 32.3853,
    THB: 33.5789,
    VND: 26252.67,
    MYR: 4.091,
    IDR: 18106.37,
    PHP: 61.6003,
    INR: 95.859,
    AED: 3.6725,
    SAR: 3.75,
    QAR: 3.64,
    KWD: 0.3102,
    BHD: 0.376,
    OMR: 0.3845,
    CHF: 0.8194,
    SEK: 9.7071,
    NOK: 9.6846,
    DKK: 6.5598,
    PLN: 3.7999,
    CZK: 21.2518,
    HUF: 316.862,
    RON: 4.6021,
    TRY: 47.3863,
    ILS: 3.0536,
    ZAR: 16.7142,
    MXN: 17.4479,
    BRL: 5.1178,
    NZD: 1.7303,
    CLP: 939.7132,
    COP: 3206.17,
    PEN: 3.4033,
    ARS: 1499.84,
    EGP: 50.5265,
    MAD: 9.3774,
    NGN: 1363.78
  };
  var ZERO_DECIMAL_CURRENCIES = {
    CLP: true,
    HUF: true,
    IDR: true,
    JPY: true,
    KRW: true,
    VND: true
  };
  var PRICE_SELECTOR = [
    '.money',
    '.price',
    '.price-list',
    '.product-item-meta__price-list-container',
    '.product-sticky-form__price',
    '.product-sticky-form__compare-price',
    '.line-item__price-list-container',
    '.mini-cart__line-item-price',
    '.mini-cart__discount-price',
    '.cart__discount-price',
    '.related-popup-price',
    '.change-price-target',
    '.change-old-price-target',
    '.change-discount-target',
    '.pc-product-card__price',
    '.pc-compare-card__price',
    '[data-raw-price]',
    '[data-slot-price]',
    '[data-product-price]'
  ].join(',');
  var CURRENCY_CODE_PATTERN = Object.keys(FALLBACK_CURRENCY_RATES).join('|');
  var MONEY_PATTERN = new RegExp('((?:' + CURRENCY_CODE_PATTERN + ')\\s*)?([$€¥₩₹₫₱£฿₺₪₦₡₲₴₽₸₼])?\\s*([0-9][0-9,.]*)(?:\\s*(' + CURRENCY_CODE_PATTERN + '))?', 'g');
  var rateCache = readRateCache();
  var activeConversion = null;
  var observer = null;
  var renderTimer = null;
  var isRendering = false;

  function optionList(select) {
    return select ? Array.prototype.slice.call(select.options) : [];
  }

  function getSelectedOption(select) {
    return select && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));

    return match ? decodeURIComponent(match[1]) : '';
  }

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; max-age=31536000; path=/; SameSite=Lax';
  }

  function readRateCache() {
    try {
      return JSON.parse(localStorage.getItem(RATE_CACHE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function writeRateCache() {
    try {
      localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(rateCache));
    } catch (error) {
      return;
    }
  }

  function getShopifyCurrency() {
    var currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency : {};
    var active = currency.active || window.themeVariables && window.themeVariables.cartCurrency || 'USD';
    var rate = parseFloat(currency.rate || '1');

    return {
      active: active,
      rate: Number.isFinite(rate) && rate > 0 ? rate : 1
    };
  }

  function getSavedCurrency() {
    var savedCurrency = localStorage.getItem(DISPLAY_CURRENCY_KEY) || '';
    var legacyCookie = getCookie('atk_language_currency');

    if (!savedCurrency && legacyCookie) {
      savedCurrency = legacyCookie.split('|')[2] || '';
    }

    return savedCurrency;
  }

  function updateSelectorUi(selector) {
    var languageSelect = selector.querySelector('[data-language-currency-language]');
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var currencyOption = getSelectedOption(currencySelect);
    var languageOption = getSelectedOption(languageSelect);
    var flagUrl = currencyOption ? currencyOption.getAttribute('data-flag-url') : '';
    var flag = selector.querySelector('[data-language-currency-select-flag]');
    var triggerFlag = selector.querySelector('[data-language-currency-trigger-flag]');
    var triggerLanguage = selector.querySelector('[data-language-currency-trigger-language]');
    var triggerCurrency = selector.querySelector('[data-language-currency-trigger-currency]');

    if (flag && flagUrl) {
      flag.setAttribute('src', flagUrl);
    }

    if (triggerFlag && flagUrl) {
      triggerFlag.setAttribute('src', flagUrl);
    }

    if (triggerLanguage && languageOption) {
      var languageLabel = languageOption.textContent.trim();
      var languageShortLabel = languageOption.getAttribute('data-language-short') || languageLabel;

      triggerLanguage.textContent = selector.hasAttribute('data-compact-language-trigger') ? languageShortLabel : languageLabel;
      triggerLanguage.setAttribute('aria-label', languageLabel);
    }

    if (triggerCurrency && currencyOption) {
      triggerCurrency.textContent = currencyOption.getAttribute('data-currency-code') || currencyOption.value;
    }
  }

  function syncCurrencySelections(currencyCode) {
    document.querySelectorAll('[data-language-currency-currency]').forEach(function(select) {
      if (currencyCode && optionList(select).some(function(option) { return option.value === currencyCode; })) {
        select.value = currencyCode;
      }

      var selector = select.closest('[data-language-currency-selector]');

      if (selector) {
        updateSelectorUi(selector);
      }
    });
  }

  function parseAmount(value) {
    var normalized = value.replace(/[^\d,.]/g, '');
    var hasComma = normalized.indexOf(',') !== -1;
    var hasDot = normalized.indexOf('.') !== -1;

    if (hasComma && hasDot) {
      normalized = normalized.replace(/,/g, '');
    } else if (hasComma && !hasDot && /,\d{1,2}$/.test(normalized)) {
      normalized = normalized.replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }

    return parseFloat(normalized);
  }

  function formatAmount(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: ZERO_DECIMAL_CURRENCIES[currencyCode] ? 0 : 2
      }).format(amount);
    } catch (error) {
      return currencyCode + ' ' + amount.toFixed(ZERO_DECIMAL_CURRENCIES[currencyCode] ? 0 : 2);
    }
  }

  function convertText(text, conversion) {
    return text.replace(MONEY_PATTERN, function(match, leadingCode, symbol, amountText, trailingCode) {
      var amount = parseAmount(amountText);

      if (!Number.isFinite(amount)) {
        return match;
      }

      if (!leadingCode && !symbol && !trailingCode && !/[.,]\d{2}$/.test(amountText)) {
        return match;
      }

      return formatAmount(amount * conversion.targetRate / conversion.activeRate, conversion.targetCurrency);
    });
  }

  function getTextNodes(element) {
    var nodes = [];
    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: function(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        if (node.parentElement && node.parentElement.closest('script, style, [data-atk-currency-skip]')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var node = walker.nextNode();

    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }

    return nodes;
  }

  function renderPrices() {
    if (!activeConversion) {
      return;
    }

    isRendering = true;

    document.querySelectorAll(PRICE_SELECTOR).forEach(function(element) {
      getTextNodes(element).forEach(function(node) {
        if (!node.__atkOriginalPriceText || node.nodeValue !== node.__atkLastRenderedPriceText) {
          node.__atkOriginalPriceText = node.nodeValue;
        }

        var nextText = activeConversion.targetCurrency === activeConversion.activeCurrency
          ? node.__atkOriginalPriceText
          : convertText(node.__atkOriginalPriceText, activeConversion);

        node.nodeValue = nextText;
        node.__atkLastRenderedPriceText = nextText;
      });
    });

    isRendering = false;
  }

  function scheduleRender() {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(renderPrices, 80);
  }

  function watchPrices() {
    if (observer || !document.body) {
      return;
    }

    observer = new MutationObserver(function() {
      if (!isRendering && activeConversion) {
        scheduleRender();
      }
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  function parseRateFromHtml(html, currencyCode) {
    var match = html.match(/Shopify\.currency\s*=\s*({.*?});/);

    if (!match) {
      return 0;
    }

    try {
      var currency = JSON.parse(match[1]);
      var rate = parseFloat(currency.rate || '0');

      return currency.active === currencyCode && Number.isFinite(rate) && rate > 0 ? rate : 0;
    } catch (error) {
      return 0;
    }
  }

  function getRate(currencyOption, activeCurrency) {
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';
    var rateUrl = currencyOption ? currencyOption.getAttribute('data-rate-url') : '';
    var fallbackRate = FALLBACK_CURRENCY_RATES[currencyCode] || 0;

    if (!currencyCode) {
      return Promise.reject();
    }

    if (currencyCode === activeCurrency) {
      return Promise.resolve(getShopifyCurrency().rate);
    }

    if (!rateUrl && fallbackRate) {
      rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
      writeRateCache();
      return Promise.resolve(fallbackRate);
    }

    if (rateCache[currencyCode] && Date.now() - rateCache[currencyCode].time < 86400000) {
      return Promise.resolve(rateCache[currencyCode].rate);
    }

    return fetch(rateUrl || '/', { credentials: 'omit' }).then(function(response) {
      return response.text();
    }).then(function(html) {
      var rate = parseRateFromHtml(html, currencyCode);

      if (!rate) {
        if (fallbackRate) {
          rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
          writeRateCache();

          return fallbackRate;
        }

        throw new Error('Missing currency rate');
      }

      rateCache[currencyCode] = { rate: rate, time: Date.now() };
      writeRateCache();

      return rate;
    }).catch(function(error) {
      if (fallbackRate) {
        rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
        writeRateCache();

        return fallbackRate;
      }

      throw error;
    });
  }

  function applyDisplayCurrency(currencySelect) {
    var currencyOption = getSelectedOption(currencySelect);
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';
    var shopifyCurrency = getShopifyCurrency();

    if (!currencyOption || !currencyCode) {
      return;
    }

    syncCurrencySelections(currencyCode);

    getRate(currencyOption, shopifyCurrency.active).then(function(targetRate) {
      activeConversion = {
        activeCurrency: shopifyCurrency.active,
        activeRate: shopifyCurrency.rate,
        targetCurrency: currencyCode,
        targetRate: targetRate
      };

      renderPrices();
      watchPrices();
    }).catch(function() {
      activeConversion = null;
    });
  }

  function initLocalizationSelector(selector) {
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var savedCurrency = getSavedCurrency();

    if (currencySelect && savedCurrency && optionList(currencySelect).some(function(option) { return option.value === savedCurrency; })) {
      currencySelect.value = savedCurrency;
    }

    updateSelectorUi(selector);

    if (currencySelect) {
      applyDisplayCurrency(currencySelect);
    }
  }

  document.querySelectorAll('[data-language-currency-selector]').forEach(initLocalizationSelector);

  document.addEventListener('change', function(event) {
    if (event.target.matches('[data-language-currency-currency]')) {
      var currencyCode = event.target.value;
      var currencyOption = getSelectedOption(event.target);
      var selector = event.target.closest('[data-language-currency-selector]');
      var countryInput = selector ? selector.querySelector('[data-language-currency-country-code]') : null;
      var countryCode = currencyOption ? currencyOption.getAttribute('data-country-code') : '';

      if (countryInput && countryCode) {
        countryInput.value = countryCode;
      }

      localStorage.setItem(DISPLAY_CURRENCY_KEY, currencyCode);
      setCookie(DISPLAY_CURRENCY_KEY, currencyCode);
      applyDisplayCurrency(event.target);
    }

    if (event.target.matches('[data-language-currency-language]')) {
      var selector = event.target.closest('[data-language-currency-selector]');
      var languageOption = getSelectedOption(event.target);
      var countryInput = selector ? selector.querySelector('[data-language-currency-country-code]') : null;
      var countryCode = languageOption ? languageOption.getAttribute('data-country-code') : '';

      if (countryInput && countryCode) {
        countryInput.value = countryCode;
      }

      if (selector) {
        updateSelectorUi(selector);
      }
    }
  });

  document.addEventListener('submit', function(event) {
    var form = event.target;
    var selector = form.querySelector('[data-language-currency-selector]');

    if (!selector) {
      return;
    }

    var languageSelect = selector.querySelector('[data-language-currency-language]');
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var countryInput = selector.querySelector('[data-language-currency-country-code]');
    var languageOption = getSelectedOption(languageSelect);
    var currencyOption = getSelectedOption(currencySelect);
    var languageCode = languageSelect ? languageSelect.value : '';
    var languageCountryCode = languageOption ? languageOption.getAttribute('data-country-code') : '';
    var currencyCountryCode = currencyOption ? currencyOption.getAttribute('data-country-code') : '';
    var currentLocaleCode = selector.getAttribute('data-current-locale') || '';
    var hasLanguageChanged = languageCode && currentLocaleCode && languageCode !== currentLocaleCode;
    var countryCode = hasLanguageChanged && languageCountryCode
      ? languageCountryCode
      : currencyCountryCode || languageCountryCode || selector.getAttribute('data-current-country');
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';

    if (!countryInput || !countryCode || !languageCode) {
      return;
    }

    countryInput.value = countryCode;

    var returnToInput = form.querySelector('input[name="return_to"]');

    if (returnToInput && hasLanguageChanged) {
      try {
        var returnToUrl = new URL(returnToInput.value || window.location.pathname + window.location.search, window.location.origin);
        returnToUrl.searchParams.set('country', countryCode);
        returnToInput.value = returnToUrl.pathname + returnToUrl.search + returnToUrl.hash;
      } catch (error) {
        returnToInput.value = window.location.pathname + '?country=' + encodeURIComponent(countryCode);
      }
    }

    if (currencyCode) {
      localStorage.setItem(DISPLAY_CURRENCY_KEY, currencyCode);
      setCookie(DISPLAY_CURRENCY_KEY, currencyCode);
    }

    document.cookie = 'atk_language_currency=' + encodeURIComponent(languageCode + '|' + countryCode + '|' + currencyCode) + '; max-age=31536000; path=/; SameSite=Lax';
  });
})();

(function() {
  var SELECTOR = '[data-language-currency-selector]';
  var OPEN_INLINE_PROPERTIES = ['visibility', 'opacity', 'transition'];
  var OPEN_VISIBLE_ELEMENTS = ['.popover__content', '.language-currency-selector__panel'];

  function getPopover(selector) {
    return selector ? selector.querySelector('.popover') : null;
  }

  function syncPopoverVisibility(selector) {
    var popover = getPopover(selector);
    var isOpen = popover && popover.hasAttribute('open');
    var visibleElements;

    if (!popover) {
      return;
    }

    selector.classList.toggle('is-atk-localization-open', isOpen);

    if (isOpen) {
      popover.style.setProperty('transition', 'none', 'important');
      popover.style.setProperty('visibility', 'visible', 'important');
      popover.style.setProperty('opacity', '1', 'important');

      visibleElements = OPEN_VISIBLE_ELEMENTS
        .map(function(item) { return popover.querySelector(item); })
        .filter(Boolean);

      visibleElements.forEach(function(element) {
        element.style.setProperty('visibility', 'visible', 'important');
        element.style.setProperty('opacity', '1', 'important');
      });
    } else {
      OPEN_INLINE_PROPERTIES.forEach(function(property) {
        popover.style.removeProperty(property);
      });

      OPEN_VISIBLE_ELEMENTS.forEach(function(item) {
        var element = popover.querySelector(item);

        if (!element) {
          return;
        }

        element.style.removeProperty('visibility');
        element.style.removeProperty('opacity');
      });
    }
  }

  function initLocalizationPopover(selector) {
    var popover = getPopover(selector);

    if (!popover || popover.dataset.atkLocalizationPopoverReady === 'true') {
      return;
    }

    popover.dataset.atkLocalizationPopoverReady = 'true';
    syncPopoverVisibility(selector);

    new MutationObserver(function() {
      syncPopoverVisibility(selector);
    }).observe(popover, { attributes: true, attributeFilter: ['open'] });
  }

  function initLocalizationPopovers(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(initLocalizationPopover);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initLocalizationPopovers(document);
    });
  } else {
    initLocalizationPopovers(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    initLocalizationPopovers(event.target);
  });

  document.addEventListener('click', function(event) {
    var selector = event.target.closest(SELECTOR);

    if (!selector) {
      return;
    }

    requestAnimationFrame(function() {
      syncPopoverVisibility(selector);
    });
  }, true);
})();

(function() {
  function setFooterSubmenuState(toggle, submenu, isOpen) {
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    submenu.hidden = !isOpen;
    submenu.toggleAttribute('open', isOpen);
    submenu.style.overflow = isOpen ? 'visible' : '';

    try {
      submenu.open = isOpen;
    } catch (error) {
      return;
    }
  }

  function initFooterSubmenus(root) {
    (root || document).querySelectorAll('.shopify-section--footer .footer__nested-toggle[aria-controls]').forEach(function(toggle) {
      var submenu = document.getElementById(toggle.getAttribute('aria-controls'));

      if (!submenu) {
        return;
      }

      setFooterSubmenuState(toggle, submenu, false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initFooterSubmenus(document);
    });
  } else {
    initFooterSubmenus(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    initFooterSubmenus(event.target);
  });

  document.addEventListener('click', function(event) {
    var toggle = event.target.closest('.shopify-section--footer .footer__nested-toggle[aria-controls]');

    if (!toggle) {
      return;
    }

    var submenu = document.getElementById(toggle.getAttribute('aria-controls'));

    if (!submenu) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    setFooterSubmenuState(toggle, submenu, toggle.getAttribute('aria-expanded') !== 'true');
  }, true);
})();

(function() {
  var GIFT_SELECTOR = '.product-offer-block--free-gift, .product-offer-block--model-gift';
  var COPY_BY_LOCALE = {
    ar: {
      'Free Gifts Available': 'الهدايا المجانية متاحة',
      'Add to cart to unlock the free gifts for your selected model.': 'أضف المنتج إلى السلة لفتح الهدايا المجانية للطراز الذي تختاره.',
      'ATK Jade Skates': 'زلاقات ماوس ATK Jade',
      'ATK Jade Mouse Skates': 'زلاقات ماوس ATK Jade',
      'ATK Esports Arm Sleeve': 'كم ذراع ATK Esports',
      'ATK Blaze Air Mouse Pad': 'لوحة ماوس ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'لوحة ماوس ATK X83 XSoft',
      'Free gift': 'هدية مجانية'
    },
    de: {
      'Free Gifts Available': 'Gratisgeschenke verfügbar',
      'Add to cart to unlock the free gifts for your selected model.': 'Lege das Produkt in den Warenkorb, um die Gratisgeschenke für dein ausgewähltes Modell freizuschalten.',
      'ATK Jade Skates': 'ATK Jade Maus-Skates',
      'ATK Jade Mouse Skates': 'ATK Jade Maus-Skates',
      'ATK Esports Arm Sleeve': 'ATK Esports Armsleeve',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air Mauspad',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft Mauspad',
      'Free gift': 'Gratisgeschenk'
    },
    es: {
      'Free Gifts Available': 'Regalos gratis disponibles',
      'Add to cart to unlock the free gifts for your selected model.': 'Añade el producto al carrito para desbloquear los regalos gratis de tu modelo seleccionado.',
      'ATK Jade Skates': 'Skates para ratón ATK Jade',
      'ATK Jade Mouse Skates': 'Skates para ratón ATK Jade',
      'ATK Esports Arm Sleeve': 'Manga de brazo ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Alfombrilla de ratón ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Alfombrilla de ratón ATK X83 XSoft',
      'Free gift': 'Regalo gratis'
    },
    fr: {
      'Free Gifts Available': 'Cadeaux offerts disponibles',
      'Add to cart to unlock the free gifts for your selected model.': 'Ajoutez le produit au panier pour débloquer les cadeaux offerts pour le modèle choisi.',
      'ATK Jade Skates': 'Patins de souris ATK Jade',
      'ATK Jade Mouse Skates': 'Patins de souris ATK Jade',
      'ATK Esports Arm Sleeve': 'Manchette esport ATK',
      'ATK Blaze Air Mouse Pad': 'Tapis de souris ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Tapis de souris ATK X83 XSoft',
      'Free gift': 'Cadeau offert'
    },
    ja: {
      'Free Gifts Available': '無料ギフト対象',
      'Add to cart to unlock the free gifts for your selected model.': 'カートに追加すると、選択したモデルの無料ギフトが有効になります。',
      'ATK Jade Skates': 'ATK Jade マウスソール',
      'ATK Jade Mouse Skates': 'ATK Jade マウスソール',
      'ATK Esports Arm Sleeve': 'ATK Esports アームスリーブ',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air マウスパッド',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft マウスパッド',
      'Free gift': '無料ギフト'
    },
    ko: {
      'Free Gifts Available': '무료 사은품 제공',
      'Add to cart to unlock the free gifts for your selected model.': '장바구니에 담으면 선택한 모델의 무료 사은품이 적용됩니다.',
      'ATK Jade Skates': 'ATK Jade 마우스 스케이트',
      'ATK Jade Mouse Skates': 'ATK Jade 마우스 스케이트',
      'ATK Esports Arm Sleeve': 'ATK Esports 암 슬리브',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air 마우스패드',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft 마우스패드',
      'Free gift': '무료 사은품'
    },
    pl: {
      'Free Gifts Available': 'Dostępne darmowe prezenty',
      'Add to cart to unlock the free gifts for your selected model.': 'Dodaj produkt do koszyka, aby odblokować darmowe prezenty dla wybranego modelu.',
      'ATK Jade Skates': 'Ślizgacze do myszy ATK Jade',
      'ATK Jade Mouse Skates': 'Ślizgacze do myszy ATK Jade',
      'ATK Esports Arm Sleeve': 'Rękaw ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Podkładka pod mysz ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Podkładka pod mysz ATK X83 XSoft',
      'Free gift': 'Darmowy prezent'
    },
    cs: {
      'Free Gifts Available': 'K dispozici jsou dárky zdarma',
      'Add to cart to unlock the free gifts for your selected model.': 'Přidejte produkt do košíku a odemkněte dárky zdarma pro vybraný model.',
      'ATK Jade Skates': 'Kluznice pro myš ATK Jade',
      'ATK Jade Mouse Skates': 'Kluznice pro myš ATK Jade',
      'ATK Esports Arm Sleeve': 'Herní návlek na paži ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Podložka pod myš ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Podložka pod myš ATK X83 XSoft',
      'Free gift': 'Dárek zdarma'
    },
    fi: {
      'Free Gifts Available': 'Ilmaisia lahjoja saatavilla',
      'Add to cart to unlock the free gifts for your selected model.': 'Lisää tuote ostoskoriin avataksesi valitsemaasi malliin kuuluvat ilmaiset lahjat.',
      'ATK Jade Skates': 'ATK Jade -hiiritassut',
      'ATK Jade Mouse Skates': 'ATK Jade -hiiritassut',
      'ATK Esports Arm Sleeve': 'ATK Esports -käsivarsihiha',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air -hiirimatto',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft -hiirimatto',
      'Free gift': 'Ilmainen lahja'
    },
    id: {
      'Free Gifts Available': 'Hadiah gratis tersedia',
      'Add to cart to unlock the free gifts for your selected model.': 'Tambahkan produk ke keranjang untuk membuka hadiah gratis untuk model pilihan Anda.',
      'ATK Jade Skates': 'Skate mouse ATK Jade',
      'ATK Jade Mouse Skates': 'Skate mouse ATK Jade',
      'ATK Esports Arm Sleeve': 'Arm sleeve ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Mouse pad ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Mouse pad ATK X83 XSoft',
      'Free gift': 'Hadiah gratis'
    },
    it: {
      'Free Gifts Available': 'Omaggi disponibili',
      'Add to cart to unlock the free gifts for your selected model.': 'Aggiungi il prodotto al carrello per sbloccare gli omaggi per il modello selezionato.',
      'ATK Jade Skates': 'Pattini per mouse ATK Jade',
      'ATK Jade Mouse Skates': 'Pattini per mouse ATK Jade',
      'ATK Esports Arm Sleeve': 'Manicotto da braccio ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Tappetino mouse ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Tappetino mouse ATK X83 XSoft',
      'Free gift': 'Omaggio'
    },
    nb: {
      'Free Gifts Available': 'Gratis gaver tilgjengelig',
      'Add to cart to unlock the free gifts for your selected model.': 'Legg produktet i handlekurven for å låse opp gratisgaver for den valgte modellen.',
      'ATK Jade Skates': 'ATK Jade museføtter',
      'ATK Jade Mouse Skates': 'ATK Jade museføtter',
      'ATK Esports Arm Sleeve': 'ATK Esports arm sleeve',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air musematte',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft musematte',
      'Free gift': 'Gratis gave'
    },
    nl: {
      'Free Gifts Available': 'Gratis cadeaus beschikbaar',
      'Add to cart to unlock the free gifts for your selected model.': 'Voeg het product toe aan je winkelwagen om de gratis cadeaus voor je gekozen model te ontgrendelen.',
      'ATK Jade Skates': 'ATK Jade muisskates',
      'ATK Jade Mouse Skates': 'ATK Jade muisskates',
      'ATK Esports Arm Sleeve': 'ATK Esports armsleeve',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air muismat',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft muismat',
      'Free gift': 'Gratis cadeau'
    },
    pt: {
      'Free Gifts Available': 'Brindes disponíveis',
      'Add to cart to unlock the free gifts for your selected model.': 'Adicione o produto ao carrinho para liberar os brindes do modelo selecionado.',
      'ATK Jade Skates': 'Skates para mouse ATK Jade',
      'ATK Jade Mouse Skates': 'Skates para mouse ATK Jade',
      'ATK Esports Arm Sleeve': 'Manga de braço ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Mouse pad ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Mouse pad ATK X83 XSoft',
      'Free gift': 'Brinde'
    },
    sv: {
      'Free Gifts Available': 'Gratisgåvor tillgängliga',
      'Add to cart to unlock the free gifts for your selected model.': 'Lägg produkten i varukorgen för att låsa upp gratisgåvorna för din valda modell.',
      'ATK Jade Skates': 'ATK Jade musfötter',
      'ATK Jade Mouse Skates': 'ATK Jade musfötter',
      'ATK Esports Arm Sleeve': 'ATK Esports arm sleeve',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air musmatta',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft musmatta',
      'Free gift': 'Gratisgåva'
    },
    tr: {
      'Free Gifts Available': 'Ücretsiz hediyeler mevcut',
      'Add to cart to unlock the free gifts for your selected model.': 'Seçtiğiniz model için ücretsiz hediyeleri açmak üzere ürünü sepete ekleyin.',
      'ATK Jade Skates': "ATK Jade mouse skate'leri",
      'ATK Jade Mouse Skates': "ATK Jade mouse skate'leri",
      'ATK Esports Arm Sleeve': 'ATK Esports kol kolluğu',
      'ATK Blaze Air Mouse Pad': 'ATK Blaze Air mouse pad',
      'ATK X83 XSoft Mouse Pad': 'ATK X83 XSoft mouse pad',
      'Free gift': 'Ücretsiz hediye'
    },
    vi: {
      'Free Gifts Available': 'Có quà tặng miễn phí',
      'Add to cart to unlock the free gifts for your selected model.': 'Thêm sản phẩm vào giỏ hàng để mở khóa quà tặng miễn phí cho mẫu bạn đã chọn.',
      'ATK Jade Skates': 'Feet chuột ATK Jade',
      'ATK Jade Mouse Skates': 'Feet chuột ATK Jade',
      'ATK Esports Arm Sleeve': 'Ống tay ATK Esports',
      'ATK Blaze Air Mouse Pad': 'Lót chuột ATK Blaze Air',
      'ATK X83 XSoft Mouse Pad': 'Lót chuột ATK X83 XSoft',
      'Free gift': 'Quà tặng miễn phí'
    }
  };

  function currentLocale() {
    return String(document.documentElement.lang || window.Shopify && window.Shopify.locale || '')
      .toLowerCase()
      .split('-')[0];
  }

  function localizeGiftOffer(root) {
    var copies = COPY_BY_LOCALE[currentLocale()];
    var walker;
    var node;
    var nodes = [];

    if (!root || !copies) {
      return;
    }

    walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    while ((node = walker.nextNode())) {
      nodes.push(node);
    }

    nodes.forEach(function(textNode) {
      var text = textNode.nodeValue.trim();

      if (copies[text]) {
        textNode.nodeValue = textNode.nodeValue.replace(text, copies[text]);
      }
    });
  }

  function initGiftOffers(root) {
    (root || document).querySelectorAll(GIFT_SELECTOR).forEach(localizeGiftOffer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initGiftOffers(document);
    });
  } else {
    initGiftOffers(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    initGiftOffers(event.target);
  });
})();
